import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Camera, Trash2, Paperclip, Eye, Pencil, Save, ChevronDown, ChevronUp } from "lucide-react";
import { api } from "../api/client";
import { Button, Field } from "./UIKit";
import { AttachmentsSection } from "./AttachmentsSection";
import { pickImage } from "../lib/imagePicker";
import {
  formatDate, formatKm, formatEuros, statusColors, statusLabel,
  RULES_LABELS, humanDelay,
} from "../lib/fleet";

const CATEGORY_OPTIONS = [
  { value: "entretien", label: "Entretien" },
  { value: "reparation", label: "Réparation" },
  { value: "pneus", label: "Pneus" },
  { value: "assurance", label: "Assurance" },
  { value: "controle_technique", label: "Contrôle technique" },
  { value: "document", label: "Document" },
  { value: "autre", label: "Autre" },
];

/**
 * HistoryDetailSheet — Bottom-sheet modal that shows the full detail of one
 * intervention (or document) with inline edit + delete + view attachment.
 *
 * Props:
 * - intervention: the intervention object (with type, date, mileage, ...)
 * - vehicle: the parent vehicle (for context)
 * - customLabel: optional label for custom_maintenance rows
 * - onClose(): close the sheet
 * - onChange(): refresh parent data after any edit / delete / attachment change
 * - onOpenLightbox(src): show a document/photo in the app-wide lightbox
 */
export function HistoryDetailSheet({ intervention, vehicle, customLabel, onClose, onChange, onOpenLightbox }) {
  const [mode, setMode] = useState("view"); // 'view' | 'edit'
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [error, setError] = useState(null);

  const [date, setDate] = useState(intervention.date || "");
  const [mileage, setMileage] = useState(intervention.mileage != null ? String(intervention.mileage) : "");
  const [garage, setGarage] = useState(intervention.garage || "");
  const [amount, setAmount] = useState(intervention.amount != null ? String(intervention.amount) : "");
  const [notes, setNotes] = useState(intervention.notes || "");
  const [title, setTitle] = useState(intervention.title || "");
  const [expiration, setExpiration] = useState(intervention.expiration_date || "");
  // Phase 4: financial details (all optional)
  const [category, setCategory] = useState(intervention.category || "");
  const [partsAmount, setPartsAmount] = useState(intervention.parts_amount != null ? String(intervention.parts_amount) : "");
  const [laborAmount, setLaborAmount] = useState(intervention.labor_amount != null ? String(intervention.labor_amount) : "");
  const [taxAmount, setTaxAmount] = useState(intervention.tax_amount != null ? String(intervention.tax_amount) : "");
  const [financeOpen, setFinanceOpen] = useState(false);

  useEffect(() => {
    setDate(intervention.date || "");
    setMileage(intervention.mileage != null ? String(intervention.mileage) : "");
    setGarage(intervention.garage || "");
    setAmount(intervention.amount != null ? String(intervention.amount) : "");
    setNotes(intervention.notes || "");
    setTitle(intervention.title || "");
    setExpiration(intervention.expiration_date || "");
    setCategory(intervention.category || "");
    setPartsAmount(intervention.parts_amount != null ? String(intervention.parts_amount) : "");
    setLaborAmount(intervention.labor_amount != null ? String(intervention.labor_amount) : "");
    setTaxAmount(intervention.tax_amount != null ? String(intervention.tax_amount) : "");
    setError(null);
  }, [intervention]);

  const label =
    intervention.type === "custom_maintenance"
      ? customLabel || "Entretien"
      : intervention.type === "autre_document"
        ? intervention.title || "Document"
        : RULES_LABELS[intervention.type] || intervention.type;

  const isDocument = [
    "assurance", "controle_technique", "carte_grise", "macaron_vtc", "autre_document",
  ].includes(intervention.type);
  const isCustomDoc = intervention.type === "autre_document";

  const save = async () => {
    if (busy) return;
    setError(null);
    const payload = {};
    if (date && date !== intervention.date) payload.date = date;
    const kmInt = mileage ? parseInt(mileage) : null;
    if (kmInt !== (intervention.mileage ?? null)) payload.mileage = kmInt;
    if ((garage || null) !== (intervention.garage || null)) payload.garage = garage || null;
    const amt = amount ? parseFloat(amount) : null;
    if (amt !== (intervention.amount ?? null)) payload.amount = amt;
    if ((notes || null) !== (intervention.notes || null)) payload.notes = notes || null;
    if (isCustomDoc && title.trim() && title.trim() !== (intervention.title || "")) payload.title = title.trim();
    // Document types: allow updating expiration_date; explicit "" → null (clear)
    if (isDocument) {
      const nextExp = expiration || null;
      const currExp = intervention.expiration_date || null;
      if (nextExp !== currExp) payload.expiration_date = nextExp;
    }
    // Phase 4 financial details — all optional. "" → null clears the field.
    if ((category || null) !== (intervention.category || null)) payload.category = category || null;
    const parts = partsAmount ? parseFloat(partsAmount) : null;
    if (parts !== (intervention.parts_amount ?? null)) payload.parts_amount = parts;
    const labor = laborAmount ? parseFloat(laborAmount) : null;
    if (labor !== (intervention.labor_amount ?? null)) payload.labor_amount = labor;
    const tax = taxAmount ? parseFloat(taxAmount) : null;
    if (tax !== (intervention.tax_amount ?? null)) payload.tax_amount = tax;
    if (Object.keys(payload).length === 0) {
      toast.info("Aucune modification");
      setMode("view");
      return;
    }
    setBusy(true);
    try {
      await api.patch(`/interventions/${intervention.id}`, payload);
      toast.success("Modifications enregistrées");
      setMode("view");
      onChange?.();
    } catch (e) {
      setError(e?.response?.data?.detail || "Enregistrement impossible");
    } finally {
      setBusy(false);
    }
  };

  // Non-blocking warning: check parts/labor/tax coherence with amount.
  const partsN = parseFloat(partsAmount) || 0;
  const laborN = parseFloat(laborAmount) || 0;
  const taxN = parseFloat(taxAmount) || 0;
  const amountN = parseFloat(amount) || 0;
  const detailSum = partsN + laborN + taxN;
  const coherenceWarn =
    detailSum > 0 && amountN > 0 && Math.abs(detailSum - amountN) > 0.01
      ? `La somme pièces + main d'œuvre + taxes (${detailSum.toFixed(2)} €) ne correspond pas au montant total (${amountN.toFixed(2)} €).`
      : null;

  const replaceAttachment = async () => {
    const img = await pickImage();
    if (!img) return;
    setBusy(true);
    try {
      await api.patch(`/interventions/${intervention.id}/attachment`, { invoice_photo: img });
      toast.success("Pièce jointe mise à jour");
      onChange?.();
    } catch {
      toast.error("Mise à jour impossible");
    } finally {
      setBusy(false);
    }
  };

  const removeAttachment = async () => {
    setBusy(true);
    try {
      await api.delete(`/interventions/${intervention.id}/attachment`);
      toast.success("Pièce jointe supprimée");
      onChange?.();
    } catch {
      toast.error("Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const deleteEntry = async () => {
    setBusy(true);
    try {
      await api.delete(`/interventions/${intervention.id}`);
      toast.success("Entrée supprimée");
      onChange?.();
      onClose?.();
    } catch {
      toast.error("Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const nextDelay = humanDelay(intervention.expiration_date);
  const statusC = intervention.status ? statusColors(intervention.status) : null;

  return (
    <div
      onClick={() => !busy && onClose?.()}
      data-testid="history-detail-sheet"
      style={{
        position: "fixed", inset: 0, background: "rgba(11,25,44,0.45)",
        display: "flex", alignItems: "flex-end", justifyContent: "center",
        zIndex: 160, padding: "20px 12px 0",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="mf-anim-in"
        style={{
          width: "100%", maxWidth: 560, background: "var(--mf-bg-card)",
          borderRadius: "24px 24px 0 0", padding: 24,
          boxShadow: "var(--mf-shadow-strong)", maxHeight: "88vh", overflowY: "auto",
          paddingBottom: `calc(24px + env(safe-area-inset-bottom))`,
        }}
      >
        <div style={{
          width: 40, height: 4, borderRadius: 2, background: "var(--mf-border)",
          margin: "0 auto 16px",
        }} />

        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 6 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "var(--mf-text)", letterSpacing: -0.3 }}>
              {label}
            </h2>
            <div style={{ marginTop: 4, fontSize: 12, color: "var(--mf-text-secondary)", fontWeight: 600, letterSpacing: 0.4 }}>
              {vehicle?.brand} {vehicle?.model} · {vehicle?.plate?.toUpperCase()}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="history-detail-close"
            style={{
              width: 36, height: 36, borderRadius: 18, border: "none",
              background: "var(--mf-bg-muted)", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <X size={18} />
          </button>
        </div>

        {statusC && (
          <div style={{ margin: "12px 0 4px" }}>
            <span style={{
              display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 10px",
              borderRadius: 999, background: statusC.bg, color: statusC.text,
              fontSize: 11, fontWeight: 800, letterSpacing: 0.2,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: statusC.solid }} />
              {statusLabel(intervention.status)}
              {nextDelay ? ` · ${nextDelay}` : ""}
            </span>
          </div>
        )}

        {mode === "view" ? (
          <>
            <dl style={{ margin: "16px 0 8px" }}>
              <DetailRow label={isDocument ? "Émise le" : "Date"} value={formatDate(intervention.date)} />
              {intervention.mileage != null && <DetailRow label="Kilométrage" value={formatKm(intervention.mileage)} />}
              {isDocument && (
                <DetailRow
                  label="Expiration"
                  value={intervention.expiration_date ? formatDate(intervention.expiration_date) : "Aucune date d'expiration"}
                  italic={!intervention.expiration_date}
                />
              )}
              {!isDocument && (intervention.next_date || intervention.next_mileage) && (
                <DetailRow
                  label="Prochaine échéance"
                  value={[
                    intervention.next_date ? formatDate(intervention.next_date) : null,
                    intervention.next_mileage ? formatKm(intervention.next_mileage) : null,
                  ].filter(Boolean).join(" · ")}
                />
              )}
              {intervention.garage && <DetailRow label="Garage" value={intervention.garage} />}
              {intervention.amount != null && <DetailRow label="Montant" value={formatEuros(intervention.amount)} />}
              {intervention.notes && <DetailRow label="Notes" value={intervention.notes} multiline />}
            </dl>

            {/* Multi-documents section — replaces the legacy single-attachment buttons */}
            <div style={{ marginTop: 16 }}>
              <AttachmentsSection interventionId={intervention.id} />
            </div>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <Button variant="secondary" fullWidth icon={<Pencil size={16} />} onClick={() => setMode("edit")} testID="history-detail-edit">
                Modifier
              </Button>
              <Button variant="danger" fullWidth icon={<Trash2 size={16} />} onClick={() => setConfirmDelete(true)} testID="history-detail-delete">
                Supprimer
              </Button>
            </div>
          </>
        ) : (
          <>
            {isCustomDoc && (
              <Field testID="history-edit-title" label="Titre" value={title} onChange={setTitle} placeholder="Ex : Contrat de location" />
            )}
            <Field testID="history-edit-date" label={isDocument ? "Date d'émission" : "Date"} type="date" value={date} onChange={setDate} />
            {isDocument && (
              <div style={{ marginBottom: 14 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
                  {"Date d'expiration (optionnel)"}
                </label>
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)}
                    data-testid="history-edit-expiration"
                    style={{
                      flex: 1, padding: "12px 14px", borderRadius: 12,
                      background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
                      color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
                    }}
                  />
                  {expiration && (
                    <button
                      type="button" onClick={() => setExpiration("")}
                      data-testid="history-edit-expiration-clear"
                      style={{
                        padding: "0 14px", borderRadius: 12,
                        background: "var(--mf-bg-muted)", border: "1px solid var(--mf-border)",
                        color: "var(--mf-text-secondary)", fontSize: 13, fontWeight: 600,
                        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6,
                      }}
                    >
                      <X size={14} /> Réinitialiser
                    </button>
                  )}
                </div>
              </div>
            )}
            <Field testID="history-edit-mileage" label="Kilométrage" value={mileage} onChange={setMileage} placeholder="185 000" />
            <Field testID="history-edit-garage" label="Garage (optionnel)" value={garage} onChange={setGarage} placeholder="Garage du Parc" />
            <Field testID="history-edit-amount" label="Montant € (optionnel)" value={amount} onChange={setAmount} placeholder="120.50" />
            <Field testID="history-edit-notes" label="Notes (optionnel)" value={notes} onChange={setNotes} multiline placeholder="…" />

            {/* Phase 4 — Détails financiers (repliable) */}
            <div style={{ marginTop: 4, marginBottom: 16 }} data-testid="finance-section">
              <button
                type="button"
                onClick={() => setFinanceOpen((v) => !v)}
                data-testid="finance-toggle"
                style={{
                  width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 12px", borderRadius: 10,
                  background: "var(--mf-bg-muted)", border: "none", color: "var(--mf-text)",
                  fontSize: 13, fontWeight: 700, cursor: "pointer",
                }}
              >
                <span>Détails financiers (optionnel)</span>
                {financeOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {financeOpen && (
                <div style={{ marginTop: 10 }} data-testid="finance-fields">
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--mf-text-secondary)", marginBottom: 6 }}>
                      Catégorie
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      data-testid="finance-category"
                      style={{
                        width: "100%", padding: "12px 14px", borderRadius: 12,
                        background: "var(--mf-bg-card)", border: "1px solid var(--mf-border)",
                        color: "var(--mf-text)", fontSize: 15, fontFamily: "inherit", outline: "none",
                      }}
                    >
                      <option value="">— Auto —</option>
                      {CATEGORY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Field testID="finance-parts" label="Pièces (€)" value={partsAmount} onChange={setPartsAmount} placeholder="50.00" />
                    <Field testID="finance-labor" label="Main-d'œuvre (€)" value={laborAmount} onChange={setLaborAmount} placeholder="70.00" />
                  </div>
                  <Field testID="finance-tax" label="Taxes / TVA (€)" value={taxAmount} onChange={setTaxAmount} placeholder="24.00" />
                  {coherenceWarn && (
                    <div data-testid="finance-warn" style={{
                      padding: "8px 12px", borderRadius: 10,
                      background: "rgba(245,158,11,0.10)", color: "#b45309",
                      fontSize: 12, fontWeight: 600,
                    }}>
                      ⚠ {coherenceWarn}
                    </div>
                  )}
                </div>
              )}
            </div>

            {error && <div style={{ color: "var(--mf-urgent-text)", fontSize: 13, marginBottom: 10 }}>{error}</div>}

            <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
              <Button variant="secondary" fullWidth onClick={() => setMode("view")} disabled={busy}>Annuler</Button>
              <Button fullWidth onClick={save} loading={busy} icon={<Save size={16} />} testID="history-edit-save">Enregistrer</Button>
            </div>
          </>
        )}

        {confirmDelete && (
          <div
            onClick={() => !busy && setConfirmDelete(false)}
            data-testid="history-delete-confirm"
            style={{
              position: "fixed", inset: 0, background: "rgba(11,25,44,0.55)",
              display: "flex", alignItems: "center", justifyContent: "center", padding: 20, zIndex: 200,
            }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{
              background: "var(--mf-bg-card)", borderRadius: 20, padding: 24, width: "100%", maxWidth: 400,
              boxShadow: "var(--mf-shadow-strong)",
            }}>
              <h3 style={{ margin: 0, marginBottom: 6, fontSize: 18, fontWeight: 800 }}>Supprimer cette entrée ?</h3>
              <p style={{ margin: "0 0 16px", color: "var(--mf-text-secondary)", fontSize: 14, lineHeight: 1.5 }}>
                {`« ${label} » sera retiré de l'historique. Cette action est irréversible mais n'affecte ni le véhicule, ni les autres entrées.`}
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <Button variant="secondary" fullWidth onClick={() => setConfirmDelete(false)} disabled={busy}>Annuler</Button>
                <Button variant="danger" fullWidth onClick={deleteEntry} loading={busy} testID="history-delete-confirm-btn">Supprimer</Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const sheetActionStyle = {
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "10px 14px", borderRadius: 999,
  background: "var(--mf-bg-muted)", border: "none", cursor: "pointer",
  color: "var(--mf-brand)", fontSize: 13, fontWeight: 700,
};

function DetailRow({ label, value, italic, multiline }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: multiline ? "flex-start" : "center",
      gap: 10, padding: "10px 0", borderBottom: "1px solid var(--mf-divider)",
    }}>
      <dt style={{ fontSize: 12, fontWeight: 700, color: "var(--mf-text-muted)", textTransform: "uppercase", letterSpacing: 0.5, minWidth: 100 }}>
        {label}
      </dt>
      <dd style={{
        margin: 0, textAlign: "right", fontSize: 14,
        color: italic ? "var(--mf-text-muted)" : "var(--mf-text)",
        fontStyle: italic ? "italic" : "normal", fontWeight: italic ? 500 : 600,
        maxWidth: "70%",
        whiteSpace: multiline ? "pre-wrap" : "normal",
      }}>{value}</dd>
    </div>
  );
}
